import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useBuilding } from '../contexts/BuildingContext';
import { canViewAllFaultReports, canManageAnnouncements, getRoleLabel, hasAccessToAllProperties } from '../utils/roleCheck';
import { LuClock, LuTrendingUp, LuUsers, LuBuilding2, LuMessageSquare, LuMegaphone } from 'react-icons/lu';
import { FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentBuilding, currentProperty } = useBuilding();
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  const isManagerOrAbove = canViewAllFaultReports(user?.role);

  useEffect(() => {
    loadDashboardData();
  }, [currentBuilding, currentProperty, user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const faultFilters = [];

      // Unified Visibility: Everyone sees property-wide stats
      if (currentProperty?.id) {
        faultFilters.push({ name: 'property_id', op: 'Equal', value: currentProperty.id });
      }

      // If a specific building is selected (not "All"), narrow down stats
      if (currentBuilding?.id && !currentBuilding.isAll) {
        if (currentBuilding.isPropertyWide) {
          faultFilters.push({ name: 'scope', op: 'Equal', value: 'PROPERTY' });
        } else {
          faultFilters.push({ name: 'building_id', op: 'Equal', value: Number(currentBuilding.id) });
        }
      }

      const { data: faultsData } = await window.ezsite.apis.tablePage(70649, {
        PageNo: 1,
        PageSize: 1000,
        Filters: faultFilters
      });

      const faults = faultsData?.List || [];
      const statsData = {
        totalReports: faults.length,
        openReports: faults.filter((f) => f.status === 'New').length,
        inProgressReports: faults.filter((f) => f.status === 'In Progress').length,
        completedReports: faults.filter((f) => f.status === 'Resolved' || f.status === 'Closed').length,
        urgentReports: faults.filter((f) => f.priority === 'High' && f.status !== 'Resolved').length
      };
      setStats(statsData);

      // Load announcements
      const announcementFilters = [];
      if (currentProperty?.id) {
        announcementFilters.push({ name: 'property_id', op: 'Equal', value: currentProperty.id });
      }

      if (currentBuilding?.id && !currentBuilding.isAll) {
        if (currentBuilding.isPropertyWide) {
          announcementFilters.push({ name: 'scope', op: 'Equal', value: 'PROPERTY' });
        } else {
          announcementFilters.push({ name: 'building_id', op: 'Equal', value: Number(currentBuilding.id) });
        }
      }

      const { data: announcementData } = await window.ezsite.apis.tablePage(70656, {
        PageNo: 1,
        PageSize: 5,
        OrderByField: 'created_at',
        IsAsc: false,
        Filters: announcementFilters
      });
      setAnnouncements(announcementData?.List || []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>);

  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
        <h1 className="text-2xl lg:text-3xl font-bold mb-2">{t('dashboard.welcomeBack', { name: user?.name })}</h1>
        <p className="text-blue-100">{t('dashboard.roleDashboard', { role: getRoleLabel(user?.role) })}</p>
        <div className="flex items-center gap-2 mt-3 text-sm">
          <LuBuilding2 className="w-4 h-4" />
          <span>
            {currentProperty?.name} 
            {currentBuilding && ` - ${currentBuilding.isAll ? t('common.all') : currentBuilding.isPropertyWide ? t('faultReports.propertyWide') : currentBuilding.name}`}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title={t('dashboard.openReports')} value={stats?.openReports || 0} icon={FiAlertTriangle} color="blue" />
        <StatCard title={t('dashboard.inProgress')} value={stats?.inProgressReports || 0} icon={LuClock} color="yellow" />
        <StatCard title={t('dashboard.completed')} value={stats?.completedReports || 0} icon={FiCheckCircle} color="green" />
        <StatCard title={t('dashboard.urgentIssues')} value={stats?.urgentReports || 0} icon={LuTrendingUp} color="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <LuMegaphone className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">{t('dashboard.latestAnnouncements')}</h2>
          </div>
          {announcements.length === 0 ?
          <p className="text-gray-500 text-sm">{t('dashboard.noAnnouncements')}</p> :

          <div className="space-y-3">
              {announcements.map((announcement) =>
            <div key={announcement.id} className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <h3 className="font-medium text-gray-900 mb-1">{announcement.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{announcement.body}</p>
                  <p className="text-xs text-gray-500 mt-2">{new Date(announcement.created_at).toLocaleDateString()}</p>
                </div>
            )}
            </div>
          }
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">{t('dashboard.quickActions')}</h2>
          <div className="grid grid-cols-2 gap-3">
            <QuickActionButton label={t('dashboard.reportIssue')} icon={FiAlertTriangle} color="blue" href="#/fault-reports" />
            <QuickActionButton label={t('sidebar.forum')} icon={LuMessageSquare} color="purple" href="#/forum" />
            {canManageAnnouncements(user?.role) &&
            <>
                <QuickActionButton label={t('dashboard.manageUsers')} icon={LuUsers} color="green" href="#/user-management" />
                <QuickActionButton label={t('sidebar.announcements')} icon={LuMegaphone} color="orange" href="#/announcements" />
              </>
            }
          </div>
        </div>
      </div>
    </div>);

}

function StatCard({ title, value, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600'
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>);

}

function QuickActionButton({ label, icon: Icon, color, href }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
    purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100',
    green: 'bg-green-50 text-green-600 hover:bg-green-100',
    orange: 'bg-orange-50 text-orange-600 hover:bg-orange-100'
  };
  return (
    <a href={href} className={`flex flex-col items-center justify-center p-4 rounded-lg transition ${colorClasses[color]}`}>
      <Icon className="w-6 h-6 mb-2" />
      <span className="text-sm font-medium text-center">{label}</span>
    </a>);

}